import { useState, useEffect, useRef } from 'react';
import { useReactTable, getCoreRowModel, ColumnDef, RowSelectionState, OnChangeFn } from '@tanstack/react-table';

type OwnProps<T> = {
  data: T[];
  columns: ColumnDef<T, any>[];
  onSelect?: OnChangeFn<RowSelectionState>;
  loadMore?: () => void;
  hasLoadMore?: boolean;
};

export function useTable<T>({ data, columns, onSelect, loadMore, hasLoadMore }: OwnProps<T>) {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const observerRef = useRef<HTMLTableRowElement | null>(null);

  const fetchNextPage = () => {
    loadMore?.();
  };

  useEffect(() => {
    if (!loadMore) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        loadMore();
      }
    });

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => observer.disconnect();
  }, [loadMore, hasLoadMore]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    state: { rowSelection },
    manualPagination: !!loadMore,
    onRowSelectionChange: (updater) => {
      setRowSelection(updater);
      onSelect?.(updater);
    },
  });
  const rows = table.getRowModel().rows;

  return { table, fetchNextPage, observerRef, rows };
}
